import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { 
  FileText,
  Search, 
  Eye,
  RefreshCw,
  DollarSign,
  CreditCard,
  Calendar,
  User,
  Phone,
  MapPin,
  CheckCircle,
  Clock,
  AlertCircle,
  Printer,
  Building,
  Pencil,
  Percent
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'gcash', label: 'GCash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'check', label: 'Check' }
];

// Users who can edit invoice prices
const PRICE_EDITORS = ['admin', 'nicole', 'kristine', 'pabs'];

const NASInvoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Check if user can edit prices
  const canEditPrices = user?.role === 'admin' || PRICE_EDITORS.includes(user?.username?.toLowerCase());
  
  // Modals
  const [isViewInvoiceOpen, setIsViewInvoiceOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isBankDepositModalOpen, setIsBankDepositModalOpen] = useState(false);
  const [isEditPricesModalOpen, setIsEditPricesModalOpen] = useState(false);
  
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Price editing state
  const [editingItems, setEditingItems] = useState([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountReason, setDiscountReason] = useState('');
  
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_method: 'cash',
    reference_number: '',
    notes: ''
  });
  
  const [bankDepositForm, setBankDepositForm] = useState({
    deposit_date: new Date().toISOString().split('T')[0],
    bank_account: '',
    reference_number: '',
    amount_deposited: 0,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const [invoicesRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/nas-invoices/invoices`, { params }),
        axios.get(`${API}/api/nas-invoices/stats`).catch(() => ({ data: null }))
      ]);
      setInvoices(invoicesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setIsLoading(false);
    }
  };

  const viewInvoice = async (invoice) => {
    try {
      const response = await axios.get(`${API}/api/nas-invoices/invoices/${invoice.id}`);
      setSelectedInvoice(response.data);
      setIsViewInvoiceOpen(true);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      amount: invoice.balance || 0,
      payment_method: 'cash',
      reference_number: '',
      notes: ''
    });
    setIsPaymentModalOpen(true);
  };

  const openBankDepositModal = (invoice) => {
    setSelectedInvoice(invoice);
    setBankDepositForm({
      deposit_date: new Date().toISOString().split('T')[0],
      bank_account: '',
      reference_number: '',
      amount_deposited: invoice.amount_paid || 0,
      notes: ''
    });
    setIsBankDepositModalOpen(true);
  };

  const handleRecordPayment = async () => {
    if (paymentForm.amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await axios.post(
        `${API}/api/nas-invoices/invoices/${selectedInvoice.id}/payment`,
        paymentForm
      );
      toast.success(`Payment of ₱${paymentForm.amount.toLocaleString()} recorded!`);
      setIsPaymentModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecordBankDeposit = async () => {
    if (!bankDepositForm.bank_account || !bankDepositForm.reference_number) {
      toast.error('Please fill in bank account and reference number');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.post(
        `${API}/api/nas-invoices/invoices/${selectedInvoice.id}/bank-deposit`,
        bankDepositForm
      );
      toast.success('Bank deposit recorded!');
      setIsBankDepositModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record bank deposit');
    } finally {
      setIsSaving(false);
    }
  };

  // Open edit prices modal
  const openEditPricesModal = (invoice) => {
    setSelectedInvoice(invoice);
    setEditingItems(invoice.items?.map(item => ({
      product_id: item.product_id,
      product_name: item.product_name,
      quantity: item.quantity,
      unit: item.unit,
      original_price: item.unit_price,
      new_price: item.unit_price,
      total: item.total
    })) || []);
    setDiscountAmount(invoice.discount_amount || 0);
    setDiscountReason(invoice.discount_reason || '');
    setIsEditPricesModalOpen(true);
  };

  // Update item price
  const updateItemPrice = (productId, newPrice) => {
    setEditingItems(prev => prev.map(item => {
      if (item.product_id === productId) {
        const price = parseFloat(newPrice) || 0;
        return {
          ...item,
          new_price: price,
          total: price * item.quantity
        };
      }
      return item;
    }));
  };

  // Calculate new totals
  const calculateNewTotal = () => {
    const subtotal = editingItems.reduce((sum, item) => sum + (item.new_price * item.quantity), 0);
    return subtotal - (discountAmount || 0);
  };

  // Save edited prices
  const handleSavePrices = async () => {
    setIsSaving(true);
    try {
      const itemUpdates = editingItems
        .filter(item => item.new_price !== item.original_price)
        .map(item => ({
          product_id: item.product_id,
          new_price: item.new_price
        }));

      await axios.put(`${API}/api/nas-invoices/invoices/${selectedInvoice.id}/prices`, {
        items: itemUpdates,
        discount_amount: discountAmount || 0,
        discount_reason: discountReason
      });
      
      toast.success('Invoice prices updated!');
      setIsEditPricesModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update prices');
    } finally {
      setIsSaving(false);
    }
  };

  const printInvoice = (invoice) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${invoice.invoice_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .header h1 { margin: 0; color: #16a34a; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
            .info-box { padding: 10px; border: 1px solid #e5e7eb; border-radius: 8px; }
            .info-box h3 { margin: 0 0 10px 0; font-size: 14px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb; }
            th { background: #f9fafb; font-weight: 600; }
            .total-row { font-weight: bold; font-size: 18px; }
            .status { padding: 4px 12px; border-radius: 20px; font-size: 12px; }
            .status-paid { background: #dcfce7; color: #16a34a; }
            .status-partial { background: #fef3c7; color: #d97706; }
            .status-unpaid { background: #fee2e2; color: #dc2626; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>NAS - Nenen's Agrivet Supply</h1>
            <p>Invoice</p>
          </div>
          
          <div class="info-grid">
            <div class="info-box">
              <h3>Invoice Details</h3>
              <p><strong>Invoice #:</strong> ${invoice.invoice_number}</p>
              <p><strong>Date:</strong> ${new Date(invoice.invoice_date).toLocaleDateString()}</p>
              <p><strong>Order:</strong> ${invoice.delivery_code}</p>
              <p><strong>Status:</strong> <span class="status status-${invoice.payment_status}">${invoice.payment_status}</span></p>
            </div>
            <div class="info-box">
              <h3>Customer</h3>
              <p><strong>${invoice.customer_name}</strong></p>
              <p>${invoice.customer_phone || ''}</p>
              <p>${invoice.customer_address || ''}</p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.items?.map(item => `
                <tr>
                  <td>${item.product_name}</td>
                  <td>${item.quantity} ${item.unit}</td>
                  <td>₱${item.unit_price?.toLocaleString()}</td>
                  <td>₱${item.total?.toLocaleString()}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="3">Total Amount</td>
                <td>₱${invoice.total_amount?.toLocaleString()}</td>
              </tr>
              <tr>
                <td colspan="3">Amount Paid</td>
                <td>₱${invoice.amount_paid?.toLocaleString()}</td>
              </tr>
              <tr class="total-row">
                <td colspan="3">Balance Due</td>
                <td>₱${invoice.balance?.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
          
          <p style="text-align: center; color: #6b7280; margin-top: 40px;">
            Thank you for your business!
          </p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-700',
      partial: 'bg-amber-100 text-amber-700',
      unpaid: 'bg-red-100 text-red-700'
    };
    const icons = {
      paid: <CheckCircle size={14} className="mr-1" />,
      partial: <Clock size={14} className="mr-1" />,
      unpaid: <AlertCircle size={14} className="mr-1" />
    };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
        {icons[status]}
        {status}
      </span>
    );
  };

  const filteredInvoices = invoices.filter(inv => 
    inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.delivery_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Invoices</h1>
          <p className="text-slate-500">Manage invoices and payment tracking</p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={isLoading}>
          <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-white">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium">Total Invoices</p>
              <p className="text-2xl font-bold text-slate-800">{stats.total_invoices}</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium">Total Amount</p>
              <p className="text-2xl font-bold text-blue-600">₱{stats.total_amount?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium">Total Paid</p>
              <p className="text-2xl font-bold text-green-600">₱{stats.total_paid?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium">Outstanding</p>
              <p className="text-2xl font-bold text-red-600">₱{stats.total_outstanding?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium">Bank Deposited</p>
              <p className="text-2xl font-bold text-purple-600">₱{stats.total_deposited?.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-10 text-center">
              <FileText className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500">No invoices found</p>
            </CardContent>
          </Card>
        ) : (
          filteredInvoices.map(invoice => (
            <Card key={invoice.id} className="bg-white hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono font-semibold text-slate-800">{invoice.invoice_number}</span>
                      {getStatusBadge(invoice.payment_status)}
                      {invoice.bank_deposit && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          <Building size={12} className="mr-1" /> Deposited
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <User size={14} />
                        {invoice.customer_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText size={14} />
                        {invoice.delivery_code}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(invoice.invoice_date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex gap-6 mt-2 text-sm">
                      <span>Total: <span className="font-semibold">₱{invoice.total_amount?.toLocaleString()}</span></span>
                      <span>Paid: <span className="font-semibold text-green-600">₱{invoice.amount_paid?.toLocaleString()}</span></span>
                      <span>Balance: <span className="font-semibold text-red-600">₱{invoice.balance?.toLocaleString()}</span></span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => viewInvoice(invoice)}>
                      <Eye size={16} className="mr-1" /> View
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => printInvoice(invoice)}>
                      <Printer size={16} className="mr-1" /> Print
                    </Button>
                    {canEditPrices && invoice.payment_status !== 'paid' && (
                      <Button size="sm" variant="outline" className="text-orange-600 border-orange-200" onClick={() => openEditPricesModal(invoice)}>
                        <Pencil size={16} className="mr-1" /> Edit Prices
                      </Button>
                    )}
                    {invoice.payment_status !== 'paid' && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => openPaymentModal(invoice)}>
                        <CreditCard size={16} className="mr-1" /> Record Payment
                      </Button>
                    )}
                    {!invoice.bank_deposit && invoice.amount_paid > 0 && (
                      <Button size="sm" variant="outline" className="text-purple-600 border-purple-200" onClick={() => openBankDepositModal(invoice)}>
                        <Building size={16} className="mr-1" /> Bank Deposit
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* View Invoice Modal */}
      <Dialog open={isViewInvoiceOpen} onOpenChange={setIsViewInvoiceOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-green-600" />
              Invoice {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Invoice Header */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="text-xs text-slate-500 font-medium mb-2">Invoice Details</h3>
                  <p className="font-mono font-semibold">{selectedInvoice.invoice_number}</p>
                  <p className="text-sm text-slate-600">Order: {selectedInvoice.delivery_code}</p>
                  <p className="text-sm text-slate-600">Date: {new Date(selectedInvoice.invoice_date).toLocaleDateString()}</p>
                  <div className="mt-2">{getStatusBadge(selectedInvoice.payment_status)}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="text-xs text-slate-500 font-medium mb-2">Customer</h3>
                  <p className="font-semibold">{selectedInvoice.customer_name}</p>
                  {selectedInvoice.customer_phone && (
                    <p className="text-sm text-slate-600 flex items-center gap-1">
                      <Phone size={12} /> {selectedInvoice.customer_phone}
                    </p>
                  )}
                  {selectedInvoice.customer_address && (
                    <p className="text-sm text-slate-600 flex items-center gap-1">
                      <MapPin size={12} /> {selectedInvoice.customer_address}
                    </p>
                  )}
                </div>
              </div>

              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3">Item</th>
                      <th className="text-right p-3">Qty</th>
                      <th className="text-right p-3">Price</th>
                      <th className="text-right p-3">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items?.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-3">{item.product_name}</td>
                        <td className="text-right p-3">{item.quantity} {item.unit}</td>
                        <td className="text-right p-3">₱{item.unit_price?.toLocaleString()}</td>
                        <td className="text-right p-3 font-medium">₱{item.total?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50">
                    <tr className="border-t">
                      <td colSpan={3} className="p-3 font-semibold">Total Amount</td>
                      <td className="text-right p-3 font-bold text-lg">₱{selectedInvoice.total_amount?.toLocaleString()}</td>
                    </tr>
                    <tr className="border-t">
                      <td colSpan={3} className="p-3">Amount Paid</td>
                      <td className="text-right p-3 font-semibold text-green-600">₱{selectedInvoice.amount_paid?.toLocaleString()}</td>
                    </tr>
                    <tr className="border-t">
                      <td colSpan={3} className="p-3 font-semibold">Balance Due</td>
                      <td className="text-right p-3 font-bold text-lg text-red-600">₱{selectedInvoice.balance?.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Payment History */}
              {selectedInvoice.payments?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <CreditCard size={16} /> Payment History
                  </h3>
                  <div className="space-y-2">
                    {selectedInvoice.payments.map((payment, idx) => (
                      <div key={idx} className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex justify-between">
                          <span className="font-semibold text-green-700">₱{payment.amount?.toLocaleString()}</span>
                          <span className="text-sm text-slate-500">{new Date(payment.recorded_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-sm text-slate-600">
                          {payment.payment_method} {payment.reference_number && `• Ref: ${payment.reference_number}`}
                        </p>
                        <p className="text-xs text-slate-500">Recorded by {payment.recorded_by}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bank Deposit */}
              {selectedInvoice.bank_deposit && (
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Building size={16} className="text-purple-600" /> Bank Deposit
                  </h3>
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-purple-600 font-medium">Deposit Date</p>
                        <p>{new Date(selectedInvoice.bank_deposit.deposit_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-purple-600 font-medium">Amount</p>
                        <p className="font-semibold">₱{selectedInvoice.bank_deposit.amount_deposited?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-purple-600 font-medium">Bank Account</p>
                        <p>{selectedInvoice.bank_deposit.bank_account}</p>
                      </div>
                      <div>
                        <p className="text-purple-600 font-medium">Reference #</p>
                        <p>{selectedInvoice.bank_deposit.reference_number}</p>
                      </div>
                    </div>
                    {selectedInvoice.bank_deposit.notes && (
                      <p className="mt-2 text-sm text-slate-600">{selectedInvoice.bank_deposit.notes}</p>
                    )}
                    <p className="mt-2 text-xs text-slate-500">Recorded by {selectedInvoice.bank_deposit.recorded_by}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => printInvoice(selectedInvoice)}>
              <Printer size={16} className="mr-1" /> Print
            </Button>
            <Button variant="outline" onClick={() => setIsViewInvoiceOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="text-green-600" />
              Record Payment
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Invoice: <span className="font-mono font-semibold">{selectedInvoice.invoice_number}</span></p>
                <p className="text-sm text-slate-500">Customer: <span className="font-semibold">{selectedInvoice.customer_name}</span></p>
                <p className="text-sm text-red-600 font-semibold">Balance Due: ₱{selectedInvoice.balance?.toLocaleString()}</p>
              </div>
              
              <div>
                <Label>Amount (₱) *</Label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value) || 0})}
                />
              </div>
              
              <div>
                <Label>Payment Method</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Reference Number</Label>
                <Input
                  value={paymentForm.reference_number}
                  onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})}
                  placeholder="e.g., GCash ref, check number..."
                />
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                Alma, Jelyn, and Nicole will be notified when this payment is recorded.
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPaymentModalOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={isSaving} className="bg-green-600 hover:bg-green-700">
              {isSaving ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Deposit Modal */}
      <Dialog open={isBankDepositModalOpen} onOpenChange={setIsBankDepositModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="text-purple-600" />
              Record Bank Deposit
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Invoice: <span className="font-mono font-semibold">{selectedInvoice.invoice_number}</span></p>
                <p className="text-sm text-slate-500">Customer: <span className="font-semibold">{selectedInvoice.customer_name}</span></p>
                <p className="text-sm text-green-600 font-semibold">Amount Paid: ₱{selectedInvoice.amount_paid?.toLocaleString()}</p>
              </div>
              
              <div>
                <Label>Deposit Date *</Label>
                <Input
                  type="date"
                  value={bankDepositForm.deposit_date}
                  onChange={(e) => setBankDepositForm({...bankDepositForm, deposit_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Bank Account *</Label>
                <Input
                  value={bankDepositForm.bank_account}
                  onChange={(e) => setBankDepositForm({...bankDepositForm, bank_account: e.target.value})}
                  placeholder="e.g., BDO Savings 1234567890"
                />
              </div>
              
              <div>
                <Label>Reference Number *</Label>
                <Input
                  value={bankDepositForm.reference_number}
                  onChange={(e) => setBankDepositForm({...bankDepositForm, reference_number: e.target.value})}
                  placeholder="Deposit slip number..."
                />
              </div>
              
              <div>
                <Label>Amount Deposited (₱) *</Label>
                <Input
                  type="number"
                  value={bankDepositForm.amount_deposited}
                  onChange={(e) => setBankDepositForm({...bankDepositForm, amount_deposited: parseFloat(e.target.value) || 0})}
                />
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={bankDepositForm.notes}
                  onChange={(e) => setBankDepositForm({...bankDepositForm, notes: e.target.value})}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBankDepositModalOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordBankDeposit} disabled={isSaving} className="bg-purple-600 hover:bg-purple-700">
              {isSaving ? 'Recording...' : 'Record Deposit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Prices Modal */}
      <Dialog open={isEditPricesModalOpen} onOpenChange={setIsEditPricesModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="text-orange-600" />
              Edit Invoice Prices
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Invoice: <span className="font-mono font-semibold">{selectedInvoice.invoice_number}</span></p>
                <p className="text-sm text-slate-500">Customer: <span className="font-semibold">{selectedInvoice.customer_name}</span></p>
                <p className="text-sm text-slate-500">Original Total: <span className="font-semibold">₱{selectedInvoice.total_amount?.toLocaleString()}</span></p>
              </div>
              
              {/* Items with editable prices */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
                  Edit Item Prices
                </div>
                <div className="divide-y">
                  {editingItems.map((item, idx) => (
                    <div key={idx} className="p-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-xs text-slate-500">{item.quantity} {item.unit}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div>
                            <Label className="text-xs text-slate-500">Unit Price</Label>
                            <Input
                              type="number"
                              value={item.new_price}
                              onChange={(e) => updateItemPrice(item.product_id, e.target.value)}
                              className="w-28 text-right"
                            />
                          </div>
                          <div className="text-right">
                            <Label className="text-xs text-slate-500">Total</Label>
                            <p className="font-semibold text-lg">₱{(item.new_price * item.quantity).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                      {item.new_price !== item.original_price && (
                        <p className="text-xs text-orange-600 mt-1">
                          Original: ₱{item.original_price?.toLocaleString()} → New: ₱{item.new_price?.toLocaleString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Discount Section */}
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2 mb-3">
                  <Percent size={18} className="text-orange-600" />
                  <Label className="font-semibold text-orange-800">Apply Discount</Label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Discount Amount (₱)</Label>
                    <Input
                      type="number"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Reason</Label>
                    <Input
                      value={discountReason}
                      onChange={(e) => setDiscountReason(e.target.value)}
                      placeholder="e.g., Loyal customer discount"
                    />
                  </div>
                </div>
              </div>
              
              {/* New Total Summary */}
              <div className="p-4 bg-slate-100 rounded-lg">
                <div className="flex justify-between text-sm mb-2">
                  <span>Subtotal:</span>
                  <span>₱{editingItems.reduce((sum, item) => sum + (item.new_price * item.quantity), 0).toLocaleString()}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-orange-600 mb-2">
                    <span>Discount:</span>
                    <span>-₱{discountAmount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>New Total:</span>
                  <span className="text-green-600">₱{calculateNewTotal().toLocaleString()}</span>
                </div>
                {selectedInvoice.amount_paid > 0 && (
                  <div className="flex justify-between text-sm text-slate-600 mt-2">
                    <span>Amount Paid:</span>
                    <span>₱{selectedInvoice.amount_paid?.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-red-600 mt-1">
                  <span>New Balance:</span>
                  <span>₱{Math.max(0, calculateNewTotal() - (selectedInvoice.amount_paid || 0)).toLocaleString()}</span>
                </div>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <strong>Note:</strong> Only Admin, Nicole, Kristine, and Pabs can edit invoice prices.
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditPricesModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePrices} disabled={isSaving} className="bg-orange-600 hover:bg-orange-700">
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASInvoices;
